import React, { useEffect, useMemo, useState } from 'react';
import ImportWorkflowScreening from 'pages/screening/ImportWorkflow';
import sanitizeModelText from 'utils/sanitizeModelText';
import { controlListsNotRunText } from 'utils/exportAssessment';
import { alertTriangleIcon } from './JurisdictionDispositionBadge';

// Appendix X niche-PGA rows (part of the control-list check). Read from the export_screening
// node, falling back to the old pga_routing location for tickets saved before the move.
const getNiche = (workflowData: any) => {
  const es = workflowData?.export_screening;
  const rows = Array.isArray(es?.additional_pga_requirements)
    ? es.additional_pga_requirements
    : Array.isArray(workflowData?.pga_routing?.additional_pga_requirements)
      ? workflowData.pga_routing.additional_pga_requirements
      : [];
  return {
    rows: rows,
    caveat: es?.additional_pga_caveat ?? workflowData?.pga_routing?.additional_pga_caveat,
    // Fall back to the legacy pga_routing location like rows/caveat above — otherwise a ticket saved
    // before the move (whose status lived on pga_routing, e.g. "no_code") loses its explanatory note.
    status: es?.additional_pga_status ?? workflowData?.pga_routing?.additional_pga_status,
  };
};

const nicheLabel = (x: any): string =>
  x?.pga_agency && x?.agency_label
    ? `${x.pga_agency} — ${x.agency_label}`
    : x?.agency_label || x?.pga_agency || 'Agency';

const nicheValue = (x: any): string => {
  const verify = x?.advisory
    ? 'Code is not unique to this agency — verify whether a permit/notification applies.'
    : 'This commodity code carries this agency’s export requirement.';
  return [x?.program, x?.permit_forms, x?.description, verify]
    .filter((v: any) => v && String(v).trim())
    .join('\n');
};

interface EARScreeningResultsProps {
  isExpands?: boolean;
  handleRowClick?: any;
  workflowData?: any;
  isSelected?: boolean;
  primary?: string;
  country?: string;
  embedded?: boolean;
  /** Card heading. This is the control-list part of the Country and Control List Check
   *  (formerly its own "Sensitive Tech Jurisdictions" phase). */
  title?: string;
}

const getFirstObject = (value: any) => {
  if (Array.isArray(value)) {
    return value.find((item) => item && typeof item === 'object') || {};
  }

  return value && typeof value === 'object' ? value : {};
};

const hasScreeningSummaryFields = (value: any) => {
  const candidate = getFirstObject(value);
  return Boolean(
    candidate?.risk_level ||
      candidate?.explanation ||
      candidate?.summary ||
      candidate?.key_factors?.length,
  );
};

const getScreeningSummary = (workflowData: any) => {
  const exportScreening = workflowData?.export_screening;
  const candidates = [
    exportScreening?.results,
    exportScreening?.result,
    exportScreening?.summary_result,
    exportScreening?.ai_summary,
    exportScreening?.aiSums,
    exportScreening?.threat_ui,
    exportScreening?.threat,
    exportScreening?.intake,
    exportScreening,
    workflowData?.results,
    workflowData?.ai_summary,
    workflowData?.aiSums,
  ];

  const match = candidates.find(hasScreeningSummaryFields);
  return getFirstObject(match);
};

const formatRiskLevel = (value?: string) => {
  if (!value) {
    return 'N/A';
  }

  const normalized = String(value).trim().toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const getRiskClass = (value?: string) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  if (normalized === 'low' || normalized === 'medium') {
    return normalized;
  }

  return normalized ? 'high' : '';
};

const normalizeWorkflowScreeningResults = (workflowResults: any) => {
  if (!workflowResults) {
    return workflowResults;
  }

  const resultWrapper = workflowResults?.result
    ? workflowResults
    : { result: workflowResults };
  const result = resultWrapper?.result;
  const hitsContainer = result?.body?.hits;
  const hits = Array.isArray(hitsContainer?.hits) ? hitsContainer.hits : [];

  if (!result?.body || !hits.length) {
    return workflowResults;
  }

  const defaultMatchedQuery = 'Screening Results';
  const normalizedHits = hits.map((hit: any) => ({
    ...hit,
    matched_queries:
      Array.isArray(hit?.matched_queries) && hit.matched_queries.length > 0
        ? hit.matched_queries
        : [defaultMatchedQuery],
  }));

  const existingBuckets = result.body?.aggregations?.lensAgg?.buckets;
  const buckets = Array.isArray(existingBuckets)
    ? existingBuckets
    : Array.from(
        normalizedHits.reduce((acc: Map<string, number>, hit: any) => {
          const lens = hit?._source?.['BITE Lens'] || 'Screening Results';
          acc.set(lens, (acc.get(lens) || 0) + 1);
          return acc;
        }, new Map<string, number>()),
      ).map(([key, doc_count]) => ({ key: key, doc_count: doc_count }));

  return {
    ...resultWrapper,
    result: {
      ...result,
      body: {
        ...result.body,
        aggregations: {
          ...result.body?.aggregations,
          lensAgg: {
            ...result.body?.aggregations?.lensAgg,
            buckets: buckets,
          },
        },
        hits: {
          ...hitsContainer,
          // WorkflowMulti reads `hits.total.value`. OpenSearch can return
          // `total` as a bare number (older response format) or as the
          // `{ value, relation }` object. Normalize to the object shape so
          // downstream consumers never end up with `undefined`/`NaN` counts.
          total:
            typeof hitsContainer?.total === 'number'
              ? { value: hitsContainer.total }
              : hitsContainer?.total ?? { value: normalizedHits.length },
          hits: normalizedHits,
        },
      },
    },
  };
};

const DetailSection = ({ title, value }: { title: string; value?: string }) => (
  <div className='classify-res-comp-sec-col'>
    <div className='classify-res-comp-sec'>
      <div className='classify-res-comp-sec-left'>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='16'
          height='16'
          viewBox='0 0 16 16'
          fill='none'
        >
          <path
            d='M4.66667 4.66667H11.3333M4.66667 8H11.3333M4.66667 11.3333H8M3.46667 14H12.5333C13.28 14 13.6534 14 13.9387 13.8547C14.1895 13.7268 14.3935 13.5229 14.5213 13.272C14.6667 12.9868 14.6667 12.6134 14.6667 11.8667V4.13333C14.6667 3.3866 14.6667 3.01323 14.5213 2.72801C14.3935 2.47713 14.1895 2.27316 13.9387 2.14532C13.6534 2 13.28 2 12.5333 2H3.46667C2.71993 2 2.34656 2 2.06135 2.14532C1.81046 2.27316 1.60649 2.47713 1.47866 2.72801C1.33333 3.01323 1.33333 3.3866 1.33333 4.13333V11.8667C1.33333 12.6134 1.33333 12.9868 1.47866 13.272C1.60649 13.5229 1.81046 13.7268 2.06135 13.8547C2.34656 14 2.71993 14 3.46667 14Z'
            stroke='var(--text-classify)'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </div>
      <div className='classify-res-comp-sec-txt'>{title}</div>
    </div>
    <div>
      <h2
        style={{
          color: 'var(--Text-Main, var(--text-primary))',
          fontSize: '14px',
          fontWeight: '400',
          lineHeight: '20px',
          paddingLeft: '28px',
          whiteSpace: 'pre-wrap',
        }}
      >
        {value || 'N/A'}
      </h2>
    </div>
  </div>
);

const EARScreeningResults: React.FC<EARScreeningResultsProps> = ({
  isExpands,
  handleRowClick,
  workflowData,
  isSelected,
  primary,
  country,
  embedded = false,
  title = 'Control lists for this product',
}) => {
  const [isExpand, setIsExpand] = useState(isExpands || false);

  useEffect(() => {
    setIsExpand(isExpands || false);
  }, [isExpands]);

  const screeningSummary = useMemo(
    () => getScreeningSummary(workflowData),
    [workflowData],
  );

  const hsCode =
    screeningSummary?.hs_code ||
    workflowData?.schedule_b?.hs_code ||
    workflowData?.schedule_b?.schedule_b_code ||
    primary ||
    '';
  const riskLevel = screeningSummary?.risk_level || '';
  const riskClass = getRiskClass(riskLevel);
  const exportScreening = workflowData?.export_screening || {};
  const hasWorkflowHits = Boolean(exportScreening?.opensearch_hits);
  const notApplicable = Boolean(exportScreening?.not_applicable);
  const naReason =
    exportScreening?.not_applicable_reason || 'Not applicable to this transaction.';
  // Appendix X niche-PGA (Other agency requirements by commodity code). "no_code" surfaces a short
  // note; "none" (a code with no niche req) self-hides; rows render the requirements.
  const niche = useMemo(() => getNiche(workflowData), [workflowData]);
  const hasNiche = niche.rows.length > 0 || niche.status === 'no_code';
  const hasResult = Boolean(
    riskLevel ||
      screeningSummary?.summary ||
      screeningSummary?.explanation ||
      screeningSummary?.key_factors?.length ||
      hasWorkflowHits ||
      hasNiche,
  );
  // Not run but settled (e.g. "Continue without a code" — the lists are matched on the code): say
  // why instead of a pending "Awaiting information..." clock (Bug 16). A real screening result wins.
  const notRunText =
    !notApplicable && !riskLevel && !screeningSummary?.summary && !screeningSummary?.explanation
      ? controlListsNotRunText(workflowData)
      : '';
  // A skipped (not-applicable) phase is settled — show a completed state + reason,
  // not a pending "Awaiting information..." clock.
  const settled = hasResult || notApplicable || Boolean(notRunText);
  const handleCardClick = () => {
    if (settled) {
      setIsExpand((prev) => !prev);
      return;
    }

    handleRowClick?.();
  };
  const handleExpandClick = (event: React.MouseEvent<SVGSVGElement>) => {
    event.stopPropagation();
    handleCardClick();
  };

  return (
    <>
      <div
        className={`classify-res-comp ${
          isSelected ? 'selected ' : ''
        }${embedded ? 'standard-workflow-ear-screening-mirror' : ''}`.trim()}
        style={{ width: '100%', minWidth: '95%' }}
      >
        {!embedded && (
          <div
            className='classify-res-comp-header'
            onClick={handleCardClick}
            style={{ cursor: 'pointer' }}
          >
          <div className='classify-res-comp-header-left-icon'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
            >
              <path
                d='M6.00008 7.66666L7.33341 9L10.3334 6M13.3334 8C13.3334 11.2723 9.76411 13.6523 8.46541 14.4099C8.31781 14.496 8.24402 14.5391 8.13987 14.5614C8.05904 14.5787 7.94112 14.5787 7.86029 14.5614C7.75615 14.5391 7.68235 14.496 7.53475 14.4099C6.23606 13.6523 2.66675 11.2723 2.66675 8V4.81173C2.66675 4.27872 2.66675 4.01222 2.75392 3.78313C2.83093 3.58076 2.95607 3.40018 3.11852 3.25702C3.30241 3.09495 3.55195 3.00138 4.05102 2.81423L7.62555 1.47378C7.76415 1.4218 7.83345 1.39582 7.90474 1.38552C7.96797 1.37638 8.03219 1.37638 8.09543 1.38552C8.16672 1.39582 8.23602 1.4218 8.37461 1.47378L11.9491 2.81423C12.4482 3.00138 12.6978 3.09495 12.8816 3.25702C13.0441 3.40018 13.1692 3.58076 13.2462 3.78313C13.3334 4.01222 13.3334 4.27872 13.3334 4.81173V8Z'
                stroke='var(--text-classify)'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
          <div className='classify-res-comp-header-text'>{title}</div>
          <div
            className='classify-res-comp-header-left-icon'
            style={{ paddingTop: '2px' }}
          >
            {notRunText ? (
              <span className='jd-warning-icon' aria-label='Not checked'>
                {alertTriangleIcon}
              </span>
            ) : settled ? (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
              >
                <path
                  d='M13.3327 4L5.99935 11.3333L2.66602 8'
                  stroke='#17B26A'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            ) : (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
              >
                <g clipPath='url(#ear_screening_clock)'>
                  <path
                    d='M8.00016 3.99992V7.99992L10.6668 9.33325M14.6668 7.99992C14.6668 11.6818 11.6821 14.6666 8.00016 14.6666C4.31826 14.6666 1.3335 11.6818 1.3335 7.99992C1.3335 4.31802 4.31826 1.33325 8.00016 1.33325C11.6821 1.33325 14.6668 4.31802 14.6668 7.99992Z'
                    stroke='#F79009'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </g>
                <defs>
                  <clipPath id='ear_screening_clock'>
                    <rect width='16' height='16' fill='white' />
                  </clipPath>
                </defs>
              </svg>
            )}
          </div>
          <div
            className='classify-res-comp-header-left-icon'
            style={{ paddingTop: '2px' }}
          >
            {!isExpand ? (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                onClick={handleExpandClick}
              >
                <path
                  d='M10 12L6 8L10 4'
                  stroke='var(--text-classify)'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            ) : (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                onClick={handleExpandClick}
              >
                <path
                  d='M4 6L8 10L12 6'
                  stroke='var(--text-classify)'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            )}
          </div>
          </div>
        )}

        {notApplicable ? (
          <p className='classification-status'>{naReason}</p>
        ) : notRunText ? (
          <p className='classification-status'>{notRunText}</p>
        ) : !isExpand ? (
          hasResult ? (
            <div
              className='classify-res-comp-body'
              onClick={embedded ? undefined : handleCardClick}
              style={{ cursor: embedded ? 'default' : 'pointer' }}
            >
              <div className='classify-res-comp-body-cont'>
                <div className='classify-res-comp-body-cont-sec'>
                  <div className='classify-res-comp-body-cont-sec-txt'>
                    Schedule B / HTS Code:
                  </div>
                  {riskLevel && (
                    <div
                      className={`classify-res-comp-body-cont-sec-icon ${riskClass}`}
                    >
                      <div className='classify-res-comp-body-cont-sec-icon-txt'>
                        {formatRiskLevel(riskLevel)}
                      </div>
                    </div>
                  )}
                </div>
                <div className='classify-res-comp-body-cont-txt'>
                  {hsCode || 'N/A'}
                </div>
              </div>
            </div>
          ) : (
            <p className='classification-status'>Awaiting information...</p>
          )
        ) : hasResult ? (
          <>
            <div className='classify-res-comp-body'>
              <div className='classify-res-comp-body-cont'>
                <div className='classify-res-comp-body-cont-sec'>
                  <div className='classify-res-comp-body-cont-sec-txt'>
                    Schedule B / HTS Code:
                  </div>
                  {riskLevel && (
                    <div
                      className={`classify-res-comp-body-cont-sec-icon ${riskClass}`}
                    >
                      <div className='classify-res-comp-body-cont-sec-icon-txt'>
                        {formatRiskLevel(riskLevel)}
                      </div>
                    </div>
                  )}
                </div>
                <div className='classify-res-comp-body-cont-txt'>
                  {hsCode || 'N/A'}
                </div>
              </div>
            </div>

            <DetailSection
              title='Summary'
              value={sanitizeModelText(screeningSummary?.summary)}
            />

            <div className='export-divider' style={{ display: 'flex' }} />

            <DetailSection
              title='Explanation'
              value={sanitizeModelText(screeningSummary?.explanation)}
            />

            <div className='export-divider' style={{ display: 'flex' }} />

            <div className='classify-res-comp-sec-col'>
              <div className='classify-res-comp-sec'>
                <div className='classify-res-comp-sec-left'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                  >
                    <path
                      d='M2 4.66667H14M2 8H14M2 11.3333H14M4.66667 2V14M11.3333 2V14'
                      stroke='var(--text-classify)'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
                <div className='classify-res-comp-sec-txt'>
                  Screening Results
                </div>
              </div>
              {hsCode && country ? (
                <ImportWorkflowScreening
                  primary={hsCode}
                  country={country}
                  isSelected={false}
                  isExpands={true}
                  resultsOnly={true}
                  workflowResults={
                    hasWorkflowHits
                      ? normalizeWorkflowScreeningResults(
                          exportScreening.opensearch_hits,
                        )
                      : undefined
                  }
                  autoOpenFirstLens={false}
                  defaultResultLevel={2}
                />
              ) : (
                <p className='classification-status'>
                  Awaiting screening inputs...
                </p>
              )}
            </div>

            {/* Appendix X — niche PGA requirements by commodity code (AMS/ATF/DEA/EPA/FWS/NMFS).
                Part of the control-list check. Shows the requirements, or a short note when
                no commodity code was classified; self-hides when a code maps to no niche req. */}
            {hasNiche && (
              <>
                <div className='export-divider' style={{ display: 'flex' }} />
                {niche.rows.length > 0 ? (
                  <>
                    <DetailSection
                      title='Other agency requirements (by commodity code)'
                      value={niche.caveat}
                    />
                    {niche.rows.map((x: any, i: number) => (
                      <DetailSection
                        key={`es-niche-${i}`}
                        title={nicheLabel(x)}
                        value={nicheValue(x)}
                      />
                    ))}
                  </>
                ) : (
                  <DetailSection
                    title='Other agency requirements (by commodity code)'
                    value='Commodity-code PGA requirements (Appendix X) could not be determined — no Schedule B/HTS code was classified.'
                  />
                )}
              </>
            )}
          </>
        ) : (
          <p className='classification-status'>Awaiting information...</p>
        )}
      </div>
    </>
  );
};

export default EARScreeningResults;
